import type { LevelCatalog } from "@highmarch/app";
import { BalanceData, Challenge, challengeTowers, decodeChallengeCode } from "@highmarch/domain";
import { GAME_NAME } from "../shareCard";
import { icon } from "../iconsKit";
import { portraitUrl, uiUrl } from "../portraits";

/** The boss face on its chip, the VIP crown on its chip (CSS px). */
const BOSS_ART_PX = 20, VIP_ART_PX = 16;

/**
 * Challenge codes' words and rule chips (MP-28, docs/MODES.md §7), shared by the Challenge sheet, the battle's intro,
 * the end screen and the share text: what a code holds, said the same way everywhere. Copy follows docs/VOICE.md.
 */

/** A clear time in sim ms as "3:07". */
export function fmtClear(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The code's twists by name ("Splitting Horde + Iron Tide"), or "" for none. */
export function twistNames(B: BalanceData, c: Challenge): string {
  return c.modifiers.map((i) => B.events.trial.modifiers[i]?.name ?? "?").join(" + ");
}

/** "11 towers · Splitting Horde · Fortress 5 HP": the rules beyond the level, one line. */
export function rulesLine(B: BalanceData, c: Challenge): string {
  const n = challengeTowers(B, c).length, twists = twistNames(B, c);
  return [`${n} tower${n === 1 ? "" : "s"}`, twists || "no twist", c.livesCap > 0 ? `Fortress ${c.livesCap} HP` : ""].filter((s) => s !== "").join(" · ");
}

/** A run's challenge from its modifiers' code, or null (not a challenge run, or a code this build cannot read). */
export function runChallenge(code: string | undefined): Challenge | null {
  if (!code) return null;
  const r = decodeChallengeCode(code);
  return r.ok ? r.challenge : null;
}

/** What a shared code says: a line a chat app shows whole, with the code to paste. */
export function challengeShareText(B: BalanceData, code: string, c: Challenge, best: string | null): string {
  const lvl = `Level ${c.level} · ${B.level(c.level).name}`;
  return `Can you beat my ${GAME_NAME} challenge? ${lvl} · ${rulesLine(B, c)}${best ? ` · my best ${best}` : ""}. Code: ${code}`;
}

/** A code's rules as chips: waves, towers and twists, the Fortress cap, the boss, VIP. */
export function rulesChipsHtml(catalog: LevelCatalog, c: Challenge): string {
  const B = catalog.balance, L = B.level(c.level), boss = B.enemies[L.bossEnemyIndex], art = portraitUrl(boss.id), n = B.modes.normalised;
  const vip = catalog.profile.vip.towerDamagePercent;
  return `<div class="chchips"><span class="chip">${icon("sword", 13)} ${B.stagesOf(c.level)} waves</span><span class="chip">${icon("tower", 13)} ${rulesLine(B, c)}</span>
    <span class="chip boss">${art ? `<img decoding="sync" src="${art}" alt="" width="${BOSS_ART_PX}" height="${BOSS_ART_PX}">` : icon("skull", 13)} ${boss.name}</span>
    <span class="chip">${icon("star", 13)} towers at level ${n.towerLevel} · no gear</span>
    ${n.allowVip && vip > 0 ? `<span class="chip vip"><img decoding="sync" src="${uiUrl("icons/vip_points.png")}" alt="" width="${VIP_ART_PX}" height="${VIP_ART_PX}"> VIP +${vip}% damage</span>` : ""}</div>`;
}
